import { useField } from 'formik'
import Styles from './GeneratedDesignPreview.module.scss'
import { useEffect, useMemo } from 'react'
import { useGenerateDesignContentSync } from 'hooks/queries/workspaces/useGenerateDesignContentSync'
import { LoadingShimmerer } from 'components/LoadingShimmerer/LoadingShimmerer'
import { DesignContentPreview } from 'components/DesignContentPreview/DesignContentPreview'
import { useChannelsListContext } from './ChannelsListContext/ChannelsListContext'
import { ChannelOption } from 'components/Wizards/CampaignWizard/Types'
import { observer } from 'mobx-react-lite'

interface Props {
  channel: ChannelOption
}

const GeneratedDesignPreview = observer(({ channel }: Props) => {
  const { updateChannels } = useChannelsListContext()
  const [{ value: topic }] = useField('topic')
  const [{ value: language }] = useField('language')
  const [{ value: brand_voice_id }] = useField('brand_voice_id')
  const [{ value: tone }] = useField('tone')
  const [{ value: brand_kit_id }] = useField('brand_kit_id')
  const [{ value: brand_visual_kit_id }] = useField('brand_visual_kit_id')
  const generateMutation = useGenerateDesignContentSync({ retry: 3 })
  const toneDep = JSON.stringify(tone)
  const { source_visual_element_id: templateId, width, height, channel_type } = channel

  useEffect(() => {
    if (!templateId) {
      return
    }
    const controller = new AbortController()
    generateMutation.mutate({
      source_visual_element_id: templateId,
      topic,
      language,
      brand_voice_id,
      tone,
      brand_kit_id,
      brand_visual_kit_id,
      signal: controller.signal,
      apply_brand: true,
    })

    return () => {
      controller.abort()
    }
  }, [templateId, topic, language, brand_voice_id, toneDep, brand_kit_id]) // eslint-disable-line react-hooks/exhaustive-deps

  const request = generateMutation.data
  const generatedContent = request?.generated_content

  const content = useMemo(() => {
    if (generatedContent) {
      return JSON.parse(generatedContent)
    }
  }, [generatedContent])

  useEffect(() => {
    updateChannels([{ channel_type, width, height, content }])
  }, [content]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className={Styles.designPreviewContainer} style={{ aspectRatio: width && height ? width / height : 1 }}>
      <LoadingShimmerer className={Styles.designPreview} show={generateMutation.isLoading || !templateId}>
        {content && (
          <div className={Styles.designPreview} data-testid={`design-content-preview-${templateId}`}>
            <DesignContentPreview content={content} />
          </div>
        )}
      </LoadingShimmerer>
    </div>
  )
})

export { GeneratedDesignPreview }
